import type {RefObject} from 'react';
import {useEffect} from 'react';
import ResizeObserver from 'resize-observer-polyfill';
import {useNavigate} from 'react-router-dom';

import {debounce} from '~ui/utils';
const LISTENER_TIMEOUT = 3000;

const CLEANUP_EVENTS = ['wheel', 'click', 'mousedown', 'keydown'];

interface ScrollOptions {
    containerRef: RefObject<HTMLElement>;
}
export function useScrollToAnchor(options: ScrollOptions) {
    const navigate = useNavigate();
    const {containerRef} = options;

    useEffect(() => {
        const location = window.location;
        const hash = location.hash.slice(1);
        const params = new URLSearchParams(location.search);
        const goanchor = params.get('goanchor');

        // Считаю goanchor приоритетнее,
        // он используется при возврате после редактирования секции
        const id = goanchor || hash;

        if (params.has('goanchor')) {
            params.delete('goanchor');
            navigate(
                {
                    search: params.toString(),
                },
                {replace: true},
            );
        }

        if (!id) {
            return undefined;
        }

        const element = containerRef.current;
        const container = element?.parentElement;

        if (!element || !container) {
            return undefined;
        }

        const debouncedScroll = debounce(() => {
            const anchorElement = container.querySelector(`a[name="${id}"], [id="${id}"]`);

            anchorElement?.scrollIntoView();
        }, 500);

        const resizeObserver = new ResizeObserver(debouncedScroll);

        resizeObserver.observe(container);

        const cleanup = () => {
            for (const eventType of CLEANUP_EVENTS) {
                container.removeEventListener(eventType, cleanup);
            }

            debouncedScroll.cancel();
            resizeObserver.unobserve(container);
            timeoutId && clearTimeout(timeoutId);
            timeoutId = null;
        };

        let timeoutId: ReturnType<typeof setTimeout> | null;

        timeoutId = setTimeout(cleanup, LISTENER_TIMEOUT);

        for (const eventType of CLEANUP_EVENTS) {
            container.addEventListener(eventType, cleanup, {
                once: true,
            });
        }

        return cleanup;
    }, [containerRef, navigate]);
}
